import serverVars from 'server-vars';
import { trackEvent, eventNameConstants } from 'dibs-tracking';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import {
    trackPageview,
    trackUserEventItemPageView,
    trackUserEventPageView,
    getUserEventItem,
} from 'dibs-tracking';
import { useSbSelector } from '../reducers/useSbSelector.ts';
import { getPageviewData } from '../utils/tracking/searchBrowse/getPageviewData.ts';

import { type SharedTrackingContainer_user$key } from './__generated__/SharedTrackingContainer_user.graphql.ts';

type GtmPageInfo = { opt_pageURL: string } | { additional: ReturnType<typeof getPageviewData> };

type Props = {
    onTrackingFired: () => void;
    user: SharedTrackingContainer_user$key | null | undefined;
    gtmPageInfo: GtmPageInfo;
    additional?: Record<string, unknown> | null;
    visitCount?: number;
    skuId?: string;
    isMultiSku?: boolean;
};

function hasAdditional(
    info: GtmPageInfo
): info is { additional: ReturnType<typeof getPageviewData> } {
    return 'additional' in info;
}

const userFragment = graphql`
    fragment SharedTrackingContainer_user on User {
        __typename
    }
`;

export const useSharedTracking = ({
    user: userRef,
    gtmPageInfo,
    isMultiSku,
    onTrackingFired = () => {},
    additional = null,
    skuId,
}: Props): void => {
    const environment = useRelayEnvironment();
    const user = useFragment(userFragment, userRef);
    const isClient = useSbSelector(state => state.relayVariables.variables.isClient);
    const fetchUser = useSbSelector(state => state.relayVariables.variables.fetchUser);

    const opt_pageURL =
        gtmPageInfo && hasAdditional(gtmPageInfo) ? gtmPageInfo.additional.opt_pageURL : null;

    const [isTrackingInitialized, setIsTrackingInitialized] = useState(false);

    // If we're fetching user but have no user data, wait until that returns before firing tracking
    const waitingForUserData = fetchUser && !user;
    const waitingForSkuId = isMultiSku && !skuId;
    const hasGtmPageInfo = !!gtmPageInfo;

    const shouldFireTracking =
        !waitingForUserData &&
        !waitingForSkuId &&
        hasGtmPageInfo &&
        !isTrackingInitialized &&
        isClient;

    useEffect(() => {
        setIsTrackingInitialized(false);
    }, [opt_pageURL, skuId]);

    useEffect(() => {
        if (isClient) {
            const seoAbTests = serverVars.get('seoAbTests');
            if (seoAbTests) {
                for (const testName in seoAbTests) {
                    if (Object.prototype.hasOwnProperty.call(seoAbTests, testName)) {
                        const variant = seoAbTests[testName];
                        // Hacking in A/B test tracking event for SEO test POC
                        // TODO: Remove once POC ends and SEO test type is added to framework
                        trackEvent({
                            category: 'a/b testing v3',
                            eventName: eventNameConstants.EVENT_AB_TESTING,
                            action: testName,
                            interaction_type: testName,
                            label: variant,
                            step_interaction_name: variant,
                            value: undefined,
                        });
                    }
                }
            }
        }
    }, [isClient]);

    const trackUserEventPageview = useCallback(
        (pageInfo?: Record<string, $TSFixMe>) => {
            if (skuId) {
                const userEventItem = getUserEventItem({
                    id: pageInfo?.additional?.itemId,
                    amount: pageInfo?.additional?.price,
                    convertedAmounts: pageInfo?.additional?.convertedAmounts,
                });
                if (userEventItem) {
                    trackUserEventItemPageView(environment, [userEventItem]);
                }
            } else {
                trackUserEventPageView(environment);
            }
        },
        [environment, skuId]
    );

    const fireTracking = useCallback(() => {
        let pageInfo: Parameters<typeof trackPageview>[0] = gtmPageInfo;
        if (additional) {
            const currentAdditional =
                gtmPageInfo && hasAdditional(gtmPageInfo) ? gtmPageInfo.additional : {};
            pageInfo = {
                ...gtmPageInfo,
                additional: {
                    ...currentAdditional,
                    ...additional,
                },
            };
        }

        trackPageview(pageInfo);
        trackUserEventPageview(pageInfo);

        setIsTrackingInitialized(true);
        onTrackingFired();
    }, [additional, gtmPageInfo, trackUserEventPageview, onTrackingFired]);

    useEffect(() => {
        if (shouldFireTracking) {
            fireTracking();
        }
    }, [shouldFireTracking, fireTracking]);
    return;
};

// Wrapper for class components
export const SharedTrackingContainer: FC<Props> = props => {
    useSharedTracking(props);
    return null;
};
